import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import SongsManager from '../components/Admin/SongsManager';

// Minimal standalone admin page for song management — scoped to just Songs
// while the full multi-tab AdminDashboard (events, CSV upload, reminders, etc.)
// stays on a separate branch. Auth is probed against /api/admin-songs itself
// (rather than a dedicated login endpoint) since that's already admin-gated.
const SongsAdmin: React.FC = () => {
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminPassword) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin-songs', {
        headers: { Authorization: `Bearer ${adminPassword}` },
      });

      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        setError('Invalid admin password');
      }
    } catch (err) {
      setError('Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <Helmet>
          <title>Songs Admin - Login</title>
        </Helmet>

        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">🎵 Songs Admin</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Password
              </label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter admin password"
                required
              />
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition duration-200 disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4 md:p-6">
      <Helmet>
        <title>Songs Admin</title>
      </Helmet>

      <div className="w-full max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl p-6 mb-6 flex justify-between items-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800">🎵 Songs Admin</h1>
          <button
            onClick={() => {
              setIsAuthenticated(false);
              setAdminPassword('');
            }}
            className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition"
          >
            🚪 Logout
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-6">
          <SongsManager adminPassword={adminPassword} />
        </div>
      </div>
    </div>
  );
};

export default SongsAdmin;
